// Composer Studio — 내장 데이터셋(dataDef)의 `join-by-key` compute 저작 순수 로직.
//
// 엔진(packages/report-composer/src/assemblers.ts joinByKey)은 base 배열 각 행에, join 배열에서
// 조인 키가 일치하는 행을 매칭해 **조인 소스의 모든 칼럼을 `조인소스경로.칼럼` flat 키로 자동
// 산출**한다(2026-07-26 접두 자동 노출). 따라서 에디터 저작 대상은 조인 구성(입력 소스·조인 키·
// 무매칭 값)뿐이다. options.mergeFields(Record<출력키, join 행 안 dotted-path>)는 레거시
// 하위호환 — 편집 UI는 철거했고, 은닉 방지를 위한 읽기 요약(readMergeFieldEntries)만 남긴다.
//
// 칼럼(def.fields) 선언은 여기서 다루지 않는다 — 조립 데이터셋의 유효 칼럼은
// `deriveComputeDataDefFields`(repeatSources.ts)가 base 상속 + 접두 칼럼 + 레거시 출력 키로
// **파생**한다.

import type { DataDef } from '@safetyhub/shared';

/** join-by-key compute의 assembler 식별자(엔진 assemblerRegistry 키). */
export const JOIN_BY_KEY_ASSEMBLER = 'join-by-key';

/** 레거시 병합 필드 한 행 — 조인 결과 행에 실릴 출력 키 ← join 소스 행 안의 dotted-path. */
export interface MergeFieldEntry {
  /** 출력 키(= 이 데이터셋의 칼럼 key). 표 셀 바인딩이 이 이름으로 값을 읽는다. */
  outKey: string;
  /** join 소스 행 안의 값 경로(예: 'currentSafetyMeasure', 'primary.note'). */
  srcPath: string;
}

/** def가 join-by-key compute를 쓰는 조립형 데이터셋이면 true. */
export function isJoinByKeyDef(def: DataDef): boolean {
  return def.compute?.assembler === JOIN_BY_KEY_ASSEMBLER;
}

/** options에서 문자열 값 한 개를 안전하게 읽는다(형상 불일치·미설정이면 undefined). */
function readStringOption(def: DataDef, key: string): string | undefined {
  const v = def.compute?.options?.[key];
  return typeof v === 'string' ? v : undefined;
}

/**
 * 조인 구성 요약(읽기 전용 표시용) — 입력 소스 키 + 양쪽 조인 키.
 * 값이 없으면 undefined(편집기가 "(미지정)"으로 표시).
 */
export interface JoinConfigSummary {
  /** inputs.base — 행 수·순서의 정본이 되는 기준 배열 키 */
  base?: string;
  /** inputs.join — 값을 실어올 조인 소스 배열 키 */
  join?: string;
  /** options.baseKey — base 행 안의 조인 키 경로 */
  baseKey?: string;
  /** options.joinKey — join 행 안의 조인 키 경로 */
  joinKey?: string;
}

/** def.compute에서 조인 구성(입력 소스 + 조인 키)을 읽는다. */
export function readJoinConfig(def: DataDef): JoinConfigSummary {
  const inputs = def.compute?.inputs ?? {};
  return {
    base: typeof inputs.base === 'string' ? inputs.base : undefined,
    join: typeof inputs.join === 'string' ? inputs.join : undefined,
    baseKey: readStringOption(def, 'baseKey'),
    joinKey: readStringOption(def, 'joinKey'),
  };
}

/**
 * 조인 구성(입력 소스 + 조인 키)을 부분 갱신한다(불변 — compute의 다른 옵션·입력 보존).
 *
 * patch에 없는 축은 손대지 않고, **빈 문자열(공백만 포함)은 "미지정"이라 그 키를 제거**한다
 * (엔진 joinByKey는 baseKey/joinKey 중 하나라도 없으면 병합 없이 base 행을 그대로 낸다 —
 * 빈 문자열을 남겨두면 "지정됐지만 매칭 안 되는" 상태가 되어 결과가 달라진다).
 * compute가 없는 def에는 아무것도 하지 않는다(호출 측이 join-by-key def만 넘긴다).
 */
export function writeJoinConfig(def: DataDef, patch: Partial<JoinConfigSummary>): DataDef {
  if (!def.compute) return def;
  const inputs: Record<string, string> = { ...(def.compute.inputs ?? {}) };
  if (patch.base !== undefined) {
    const t = patch.base.trim();
    if (t) inputs.base = t;
    else delete inputs.base;
  }
  if (patch.join !== undefined) {
    const t = patch.join.trim();
    if (t) inputs.join = t;
    else delete inputs.join;
  }
  const options: Record<string, unknown> = { ...(def.compute.options ?? {}) };
  if (patch.baseKey !== undefined) {
    const t = patch.baseKey.trim();
    if (t) options.baseKey = t;
    else delete options.baseKey;
  }
  if (patch.joinKey !== undefined) {
    const t = patch.joinKey.trim();
    if (t) options.joinKey = t;
    else delete options.joinKey;
  }
  // inputs가 전부 비면 키 자체를 떨어뜨린다(빈 객체를 남기지 않음 — 저장 형상 정리).
  const compute: NonNullable<DataDef['compute']> = { assembler: def.compute.assembler, options };
  if (Object.keys(inputs).length > 0) compute.inputs = inputs;
  return { ...def, compute };
}

/**
 * 무매칭 병합값(options.emptyValue) — 미지정/문자열 아님이면 ''(엔진 기본값과 동일).
 * 문자열이 아닌 값(숫자 등)은 편집기가 다루지 않으므로 ''로 보이되, 저장은 건드리지 않는다
 * (writeEmptyValue를 호출할 때만 덮어써진다).
 */
export function readEmptyValue(def: DataDef): string {
  const v = def.compute?.options?.emptyValue;
  return typeof v === 'string' ? v : '';
}

/**
 * 무매칭 병합값을 되쓴다(불변). 빈 문자열이면 키를 제거 — 엔진은 미설정 시 ''를 쓰므로 동작 동일하고
 * 저장 형상이 깨끗해진다.
 */
export function writeEmptyValue(def: DataDef, value: string): DataDef {
  if (!def.compute) return def;
  const options: Record<string, unknown> = { ...(def.compute.options ?? {}) };
  if (value) options.emptyValue = value;
  else delete options.emptyValue;
  return { ...def, compute: { ...def.compute, options } };
}

/**
 * 조인 입력 소스 키가 알려진 소스 목록(내장 데이터셋/카탈로그 배열/조립 데이터 소스)에 없으면 true.
 * 빈 값(미지정)은 경고 대상이 아니다 — "아직 안 고름"과 "잘못 참조"는 다른 안내가 붙는다.
 */
export function isUnknownSourceKey(key: string | undefined, knownKeys: readonly string[]): boolean {
  const k = key?.trim();
  if (!k) return false;
  return !knownKeys.includes(k);
}

/**
 * 기존 키와 충돌하지 않는 데이터셋 key(`<prefix>-1`, `<prefix>-2`, ...).
 *
 * `reserved`는 **다른 저장 배열의 key**(조립 데이터 소스 등) — 에디터에서 두 배열이 한 "데이터"
 * 목록으로 보이므로(2026-07-26 표면 통합) 자동 생성 key도 양쪽을 함께 피해야 한다.
 * 넘기지 않으면 종전대로 defs 안에서만 회피한다.
 */
export function nextDataDefKey(
  defs: DataDef[],
  prefix: string,
  reserved?: ReadonlySet<string>,
): string {
  const taken = (k: string) => defs.some((d) => d.key === k) || !!reserved?.has(k);
  let n = defs.length + 1;
  let key = `${prefix}-${n}`;
  while (taken(key)) key = `${prefix}-${++n}`;
  return key;
}

/**
 * 새 join-by-key 데이터셋 뼈대 — 조인 구성(소스·조인 키·무매칭 값)은 편집기에서 채운다.
 * 칼럼은 저작 대상이 아니다(조인 소스 칼럼은 엔진이 접두 flat 키로 자동 노출).
 * inputs/options는 비운 채 두고 writeJoinConfig가 필요한 키만 심는다(빈 키 잔재 방지).
 */
export function newJoinByKeyDef(key: string, label: string): DataDef {
  return {
    key,
    label,
    fields: [],
    defaultRows: [],
    compute: { assembler: JOIN_BY_KEY_ASSEMBLER, options: {} },
  };
}

/**
 * 조립(compute) 데이터셋의 잔존 칼럼 선언을 로드 시 정규화로 비운다(2026-07-26 사용자 확정).
 * 조인 구성이 소스를 정의하므로 결과 칼럼은 전부 파생 가능(base 상속 + 접두 자동 노출) —
 * 선언은 저작 대상이 아니고, 남겨두면 UI 노출로 복잡성만 더한다. 다음 저장 때 DB도 함께
 * 정리된다(에디터 저작 경유 — DB 직접 패치 아님). 비-compute(직접 입력) 선언은 정본이라 보존.
 */
export function stripComputeDefFields(defs: DataDef[] | undefined): DataDef[] | undefined {
  if (!defs) return defs;
  return defs.map((d) => (d.compute && d.fields.length > 0 ? { ...d, fields: [] } : d));
}

/**
 * 편집 UI가 없는 조립 방식(zip-two-sources/slice-range 등)의 읽기 전용 요약 줄들.
 * 최소한 **조회는 가능**해야 은닉 설정이 되지 않는다 — 값 변경은 마이그레이션/DB 경로.
 */
export function summarizeComputeLines(def: DataDef): string[] {
  const c = def.compute;
  if (!c) return [];
  const lines = [`assembler = ${c.assembler}`];
  for (const [k, v] of Object.entries(c.inputs ?? {})) lines.push(`inputs.${k} = ${v}`);
  for (const [k, v] of Object.entries(c.options ?? {})) {
    lines.push(`options.${k} = ${typeof v === 'string' ? v : JSON.stringify(v)}`);
  }
  return lines;
}

/**
 * def.compute.options.mergeFields(레거시)를 행 배열로 읽는다(선언 순서 보존) — 읽기 전용 요약용.
 * 값이 문자열이 아닌 항목은 잘못 저작된 것이라 건너뛴다(엔진도 dotted-path 문자열만 소비).
 * (2026-07-26 접두 자동 노출 개편으로 편집(writeMergeFieldEntries 등)은 철거 — 신규 저작 없음.)
 */
export function readMergeFieldEntries(def: DataDef): MergeFieldEntry[] {
  const raw = def.compute?.options?.mergeFields;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return [];
  const out: MergeFieldEntry[] = [];
  for (const [outKey, srcPath] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof srcPath !== 'string') continue;
    out.push({ outKey, srcPath });
  }
  return out;
}
